import posthog from "posthog-js";

import { requestData } from "@/api/api-config";

// TODO: Error tracking improvements (Feb 2026)
//
// Current state:
// - logErrorToBackend() sends errors to the backend POST /errors/log endpoint
//   (decoda/apps/api/error.py), which posts them to Slack #error-reporting.
// - Stack traces are minified in production, making Slack error reports hard to debug.
// - posthog.captureException() in error.tsx captures errors, but our self-hosted PostHog
//   does not support error tracking with source maps yet. The "cymbal" service required
//   for processing $exception events is not available in self-hosted PostHog.
//   See: https://github.com/PostHog/posthog/issues/38918
//
// What's already set up (waiting on PostHog self-hosted support):
// - next.config.mjs has productionBrowserSourceMaps enabled
// - Dockerfile uploads source maps to PostHog via CLI during build, then deletes them
// - CI passes PostHog credentials as Docker secrets
// - Once PostHog self-hosted supports error tracking, source maps will resolve automatically
//
// Alternatives if we need readable stack traces sooner:
// - Add Sentry (free tier, works immediately, best source map support)
// - Use PostHog Cloud for error tracking only (while keeping self-hosted for analytics)

interface ErrorLogPayload {
  message: string;
  stack?: string;
  source: string;
  component?: string;
  context?: Record<string, any>;
  url?: string;
  path?: string;
  userAgent?: string;
  timestamp: string;
  posthogReplayUrl?: string;
}

/**
 * Log an error to the backend POST /errors/log endpoint,
 * which posts it to Slack #error-reporting. See TODO above for context.
 *
 * `extraHeaders` is used by the client-side rate limiter to mark its own
 * self-alert with a bypass header so it isn't blocked by the very limit
 * that just fired. App code should not pass this.
 */
export async function logErrorToBackend(
  error: Error | unknown,
  source: string = "unknown",
  context?: Record<string, any>,
  extraHeaders?: Record<string, string>,
): Promise<void> {
  try {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    const payload: ErrorLogPayload = {
      message: errorObj.message,
      stack: errorObj.stack,
      source,
      context,
      timestamp: new Date().toISOString(),
    };

    // Add browser-specific information when available
    if (typeof window !== "undefined") {
      payload.url = window.location.href;
      payload.path = window.location.pathname;
      payload.userAgent = navigator.userAgent;

      // Attach the current PostHog session replay URL so the Slack alert can
      // link straight to a recording of what the user did before the error.
      // Session recording runs globally (see instrumentation-client.ts), so a
      // recording is normally already active by the time an error fires.
      try {
        const replayUrl = posthog.get_session_replay_url?.({
          withTimestamp: true,
        });
        if (replayUrl) {
          payload.posthogReplayUrl = replayUrl;
        }
      } catch {
        // PostHog may be uninitialized (missing config, blocked in E2E, etc.).
        // Never let replay-URL capture break error reporting itself.
      }
    }

    await requestData("errors/log", "post", {
      json: payload,
      timeout: 5000, // Set a short timeout for error logging
      ...(extraHeaders ? { headers: extraHeaders } : {}),
    });
  } catch (loggingError) {
    // Don't throw errors from the error logger itself
    console.error("Failed to log error to backend:", loggingError);
  }
}
